/**
 * The world track (D287): a track from the barn door to each producer's plot, opened as the plot is
 * unlocked, and a lane from the door out to the world gate, which always crosses the river on a
 * bridge. Worked out from the layout alone, so the same seed gives the same track. Nothing that
 * earns reads it.
 *
 * The track runs along the lines between tiles, never through a plot, the yard or a festival mark:
 * a point `{ x, y }` here is a tile corner, so tile `x,y` spans corners `x,y` to `x+1,y+1`. It
 * keeps to the world and its margin in front and to the sides, never behind, where the marks grow.
 * Routes are the cheapest by length, with a small cost for each turn so they run straight, and a
 * large cost for each step over water so the river is crossed once, at its narrowest.
 */
import type { IdleContent } from '@hedgerow/data';
import { idleLayout, idleMarkTile, riverMaxOut, riverReach, riverSide } from './layout.ts';
import type { IdleWorldState, IdleLayout, IdleLayoutRect, IdleLayoutTile } from './state.ts';

/** One stretch of track: from the barn door to its end, corner by corner. */
export interface IdleTrackSegment {
  /** A producer's id, or `gate` for the lane out to the world gate. */
  to: string;
  /** Tile corners from the barn door to the plot's nearest corner, or to the gate. */
  points: IdleLayoutTile[];
}

/** One step of track between two neighbouring corners. `wet` steps are on a bridge. */
export interface IdleTrackEdge {
  from: IdleLayoutTile;
  to: IdleLayoutTile;
  wet: boolean;
}

/** Where the track crosses the river: a bridge from one dry corner to the other. */
export interface IdleTrackBridge {
  from: IdleLayoutTile;
  to: IdleLayoutTile;
}

export interface IdleTrack {
  /** The surface's id from `content.track.surfaces`: dirt, then gravel, then stone. */
  surface: string;
  /** The barn door: the middle of the yard's front edge. */
  door: IdleLayoutTile;
  /** The world gate, on the map's edge on the river's side. */
  gate: IdleLayoutTile;
  /** The lane to the gate first, then each unlocked producer's track, in content order. */
  segments: IdleTrackSegment[];
  /** Every step of the open track once, for drawing, in the order the segments first take them. */
  edges: IdleTrackEdge[];
  /** Where the open track crosses the river. */
  bridges: IdleTrackBridge[];
}

/** The whole plan: every route, open or not. */
export interface IdleTrackPlan {
  door: IdleLayoutTile;
  gate: IdleLayoutTile;
  /** The lane from the door to the gate. */
  lane: IdleLayoutTile[];
  /** Each producer with a plot, by id: corners from the door to its plot. */
  routes: Record<string, IdleLayoutTile[]>;
}

const STEP = 2;
const TURN = 1;
const WET = 60;

const key = (t: IdleLayoutTile) => `${t.x},${t.y}`;

/** The cheapest routes from the barn door over the corner grid. Pure; the same layout, the same plan. */
export function idleTrackPlan(layout: IdleLayout): IdleTrackPlan {
  const cached = plans.get(layout);
  if (cached) return cached;
  const { width: W, height: H, margin: M } = layout;
  // D319: the grid's edge follows the river's own reach, so there is no dry gap past its end, or
  // round its corner, for a route to slip past the water through. The river's own bend (`out`)
  // sets how far the grid runs on the side it hugs; its corner wrap (always `riverMaxOut`, D319)
  // sets how far the grid runs on the sides it only passes the end of.
  const river = layout.river ?? layout.stream;
  const side = river.length > 0 ? riverSide(layout) : -1;
  const wrap = riverMaxOut(M);
  let boundWest = M;
  let boundEast = M;
  let boundFront = M;
  if (side === 0) {
    boundWest = riverReach(layout, 0) + 1;
    boundFront = wrap;
  } else if (side === 1) {
    boundEast = riverReach(layout, 1) + 1;
    boundFront = wrap;
  } else if (side === 2) {
    boundFront = riverReach(layout, 2) + 1;
    boundWest = wrap;
    boundEast = wrap;
  }
  const X0 = -boundWest;
  const cols = W + boundWest + boundEast + 1;
  const rows = H + boundFront + 1;
  const inGrid = (x: number, y: number) =>
    x >= X0 && x <= W + boundEast && y >= 0 && y <= H + boundFront;
  const node = (x: number, y: number) => y * cols + (x - X0);

  // D344: the pond is never crossed either.
  const blocks: IdleLayoutRect[] = [
    layout.yard,
    ...Object.values(layout.plots),
    ...(layout.pond ? [layout.pond] : []),
  ];
  for (let i = 0; i < layout.marks.perRow; i++) {
    const m = idleMarkTile(layout, i);
    if (m.y >= 0) blocks.push(m);
  }
  const water = new Set((layout.river ?? layout.stream).map(key));
  const wetTile = (x: number, y: number) => water.has(`${x},${y}`);

  // A step from (x, y) one along `d`: 0 east, 1 south, 2 west, 3 north.
  const DX = [1, 0, -1, 0];
  const DY = [0, 1, 0, -1];
  /** Blocked when it runs through the inside of a plot, the yard or a mark. */
  const blocked = (x: number, y: number, d: number): boolean => {
    const nx = x + DX[d]!;
    const ny = y + DY[d]!;
    const lx = Math.min(x, nx);
    const ly = Math.min(y, ny);
    if (y === ny)
      return blocks.some((r) => r.x <= lx && lx + 1 <= r.x + r.w && r.y < y && y < r.y + r.h);
    return blocks.some((r) => r.x < x && x < r.x + r.w && r.y <= ly && ly + 1 <= r.y + r.h);
  };
  /** Wet when a tile either side of the step is river. */
  const wet = (x: number, y: number, d: number): boolean => {
    const lx = Math.min(x, x + DX[d]!);
    const ly = Math.min(y, y + DY[d]!);
    return DY[d] === 0
      ? wetTile(lx, ly - 1) || wetTile(lx, ly)
      : wetTile(lx - 1, ly) || wetTile(lx, ly);
  };

  const yard = layout.yard;
  const door = { x: yard.x + Math.floor(yard.w / 2), y: yard.y + yard.h };

  // Cheapest cost to each (corner, heading), with a small heap.
  const n = cols * rows * 4;
  const dist = new Float64Array(n).fill(Infinity);
  const prev = new Int32Array(n).fill(-1);
  const heap: number[] = [];
  const less = (a: number, b: number) => dist[a]! < dist[b]! || (dist[a] === dist[b] && a < b);
  const push = (s: number) => {
    heap.push(s);
    let i = heap.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!less(heap[i]!, heap[p]!)) break;
      [heap[i], heap[p]] = [heap[p]!, heap[i]!];
      i = p;
    }
  };
  const pop = (): number => {
    const top = heap[0]!;
    const last = heap.pop()!;
    if (heap.length > 0) {
      heap[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let m = i;
        if (l < heap.length && less(heap[l]!, heap[m]!)) m = l;
        if (r < heap.length && less(heap[r]!, heap[m]!)) m = r;
        if (m === i) break;
        [heap[i], heap[m]] = [heap[m]!, heap[i]!];
        i = m;
      }
    }
    return top;
  };
  for (let d = 0; d < 4; d++) {
    const s = node(door.x, door.y) * 4 + d;
    dist[s] = 0;
    push(s);
  }
  const done = new Uint8Array(n);
  while (heap.length > 0) {
    const s = pop();
    if (done[s]) continue;
    done[s] = 1;
    const at = s >> 2;
    const hd = s & 3;
    const x = (at % cols) + X0;
    const y = Math.floor(at / cols);
    for (let d = 0; d < 4; d++) {
      const nx = x + DX[d]!;
      const ny = y + DY[d]!;
      if (!inGrid(nx, ny) || blocked(x, y, d)) continue;
      const cost = (wet(x, y, d) ? WET : STEP) + (d === hd ? 0 : TURN);
      const t = node(nx, ny) * 4 + d;
      if (dist[s]! + cost < dist[t]!) {
        dist[t] = dist[s]! + cost;
        prev[t] = s;
        push(t);
      }
    }
  }

  /** The best heading state at a corner, and its cost. */
  const best = (x: number, y: number): { s: number; cost: number } => {
    let s = -1;
    let cost = Infinity;
    for (let d = 0; d < 4; d++) {
      const t = node(x, y) * 4 + d;
      if (dist[t]! < cost) {
        cost = dist[t]!;
        s = t;
      }
    }
    return { s, cost };
  };
  const route = (s: number): IdleLayoutTile[] => {
    const out: IdleLayoutTile[] = [];
    let c = s;
    let last = -1;
    while (c >= 0) {
      const at = c >> 2;
      if (at !== last) out.push({ x: (at % cols) + X0, y: Math.floor(at / cols) });
      last = at;
      c = prev[c]!;
    }
    return out.reverse();
  };

  const routeTo = (r: IdleLayoutRect): IdleLayoutTile[] => {
    // The plot's nearest corner, the one further back and then further west on a tie.
    let pick = { s: -1, cost: Infinity };
    const corners = [
      { x: r.x, y: r.y },
      { x: r.x + r.w, y: r.y },
      { x: r.x, y: r.y + r.h },
      { x: r.x + r.w, y: r.y + r.h },
    ];
    for (const c of corners) {
      if (!inGrid(c.x, c.y)) continue;
      const b = best(c.x, c.y);
      if (b.cost < pick.cost) pick = b;
    }
    return pick.s >= 0 ? route(pick.s) : [door];
  };
  const routes: Record<string, IdleLayoutTile[]> = {};
  for (const [id, r] of Object.entries(layout.plots)) routes[id] = routeTo(r);

  const gate = gateFor(layout, door);
  const g = best(gate.x, gate.y);
  const lane = g.s >= 0 ? route(g.s) : [door];
  const plan = { door, gate, lane, routes };
  plans.set(layout, plan);
  return plan;
}

const plans = new WeakMap<IdleLayout, IdleTrackPlan>();

/**
 * The world gate: on the map's edge on the river's side, one step past its farthest bend there, so
 * the lane to it always crosses the water (D319: the river may sit closer in than the full
 * margin, so the gate follows its actual reach rather than assuming the margin itself).
 */
function gateFor(layout: IdleLayout, door: IdleLayoutTile): IdleLayoutTile {
  const { width: W, height: H, margin: M } = layout;
  const river = layout.river ?? layout.stream;
  if (river.length === 0)
    // No river: the gate on the yard's own side.
    return layout.mirrored ? { x: W + M, y: door.y } : { x: -M, y: door.y };
  const side = riverSide(layout);
  const past = riverReach(layout, side) + 1;
  if (side === 0) return { x: -past, y: door.y };
  if (side === 1) return { x: W + past, y: door.y };
  return { x: door.x, y: H + past };
}

/** The track's surface: the last in `content.track.surfaces` whose `owned` this year has reached. */
export function idleTrackSurface(state: IdleWorldState, content: IdleContent): string {
  const owned = Object.values(state.year.producers).reduce((a, p) => a + p.owned, 0);
  let id = content.track.surfaces[0]!.id;
  for (const s of content.track.surfaces) if (owned >= s.owned) id = s.id;
  return id;
}

/**
 * D301: a straight, unit-step run from `a` to `b` (never diagonal), for the spur out to a
 * piece of land beside the world. The land sits past the margin the main grid keeps to, so
 * this is walked directly rather than routed: the land is open ground, never another plot
 * in the way, and the gate already stands on the land's own side of the river.
 */
function straightRoute(a: IdleLayoutTile, b: IdleLayoutTile): IdleLayoutTile[] {
  const out: IdleLayoutTile[] = [{ ...a }];
  let { x, y } = a;
  const dx = Math.sign(b.x - x);
  while (x !== b.x) {
    x += dx;
    out.push({ x, y });
  }
  const dy = Math.sign(b.y - y);
  while (y !== b.y) {
    y += dy;
    out.push({ x, y });
  }
  return out;
}

/** The corner of `r` nearest `from`, by the steps to walk there. */
function nearestCorner(r: IdleLayoutRect, from: IdleLayoutTile): IdleLayoutTile {
  const corners = [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x, y: r.y + r.h },
    { x: r.x + r.w, y: r.y + r.h },
  ];
  return corners.reduce((best, c) =>
    Math.abs(c.x - from.x) + Math.abs(c.y - from.y) <
    Math.abs(best.x - from.x) + Math.abs(best.y - from.y)
      ? c
      : best,
  );
}

/**
 * The open track (D287): the lane to the gate, always, and a track to each unlocked producer's
 * plot, with its surface and the bridges where it crosses the river. D301: a piece of land
 * beside the world, open, gets its own spur out from the gate. D348: a theme with no track
 * (`map.terrain.track` false) has its door and gate but no segments, edges or bridges.
 */
export function idleTrackFor(state: IdleWorldState, content: IdleContent): IdleTrack {
  const layout = idleLayout(state, content);
  const plan = idleTrackPlan(layout);
  if (!content.map.terrain.track)
    return {
      surface: idleTrackSurface(state, content),
      door: plan.door,
      gate: plan.gate,
      segments: [],
      edges: [],
      bridges: [],
    };
  const segments: IdleTrackSegment[] = [{ to: 'gate', points: plan.lane }];
  for (const p of content.producers)
    if (state.year.producers[p.id] && plan.routes[p.id])
      segments.push({ to: p.id, points: plan.routes[p.id]! });
  for (const l of Object.values(layout.lands ?? {}))
    for (const [id, r] of Object.entries(l.plots))
      if (state.year.producers[id])
        segments.push({ to: id, points: straightRoute(plan.gate, nearestCorner(r, plan.gate)) });

  const water = new Set((layout.river ?? layout.stream).map(key));
  const wetStep = (a: IdleLayoutTile, b: IdleLayoutTile): boolean => {
    const lx = Math.min(a.x, b.x);
    const ly = Math.min(a.y, b.y);
    return a.y === b.y
      ? water.has(`${lx},${ly - 1}`) || water.has(`${lx},${ly}`)
      : water.has(`${lx - 1},${ly}`) || water.has(`${lx},${ly}`);
  };

  const edges: IdleTrackEdge[] = [];
  const bridges: IdleTrackBridge[] = [];
  const seenEdge = new Set<string>();
  const seenBridge = new Set<string>();
  for (const seg of segments) {
    let start: IdleLayoutTile | undefined;
    for (let i = 1; i < seg.points.length; i++) {
      const a = seg.points[i - 1]!;
      const b = seg.points[i]!;
      const w = wetStep(a, b);
      const k = [key(a), key(b)].sort().join('|');
      if (!seenEdge.has(k)) {
        seenEdge.add(k);
        edges.push({ from: a, to: b, wet: w });
      }
      if (w && !start) start = a;
      if (start && (!w || i === seg.points.length - 1)) {
        const end = w ? b : a;
        const bk = [key(start), key(end)].sort().join('|');
        if (!seenBridge.has(bk)) {
          seenBridge.add(bk);
          bridges.push({ from: start, to: end });
        }
        start = undefined;
      }
    }
  }
  return {
    surface: idleTrackSurface(state, content),
    door: plan.door,
    gate: plan.gate,
    segments,
    edges,
    bridges,
  };
}
